import React, { useEffect, useMemo, useState } from "react";
import {
	Card,
	Space,
	Typography,
	Select,
	Button,
	Table,
	Input,
	Tag,
	Modal,
	message,
	Switch,
} from "antd";
import {
	ReloadOutlined,
	SaveOutlined,
	EyeOutlined,
	SearchOutlined,
} from "@ant-design/icons";
import {
	get_templates_catalog,
	set_client_templates,
	sync_client_templates,
} from "../../../../api/endpoints/agents";
import { get_clients_name_list_agent_panel } from "../../../../api/endpoints/clients";
import { Inner, Section } from "./styles";
import Main from "../../../layout/main/Main";
import { Link, useSearchParams } from "react-router-dom";
import SecondaryButton from "../../../common/antd-buttons/SecondaryButton";
import DefaultButton from "../../../common/antd-buttons/DefaultButton";
import PrimaryButton from "../../../common/antd-buttons/PrimaryButton";
import AntdSelect from "../../../common/antd-select/AntdSelect";
import AntdCheckbox from "../../../common/antd-checkbox/AntdCheckbox";

export default function ClientTemplatesAdminPage() {
	const [searchParams] = useSearchParams();
	const uri = searchParams.get("uri");
	const [loading, setLoading] = useState(false);
	const [syncLoading, setSyncLoading] = useState(false);

	const [clients, setClients] = useState([]);
	const [clientId, setClientId] = useState(null);

	const [templates, setTemplates] = useState([]);
	const [dirty, setDirty] = useState(false);

	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewTemplate, setPreviewTemplate] = useState(null);

	const [search, setSearch] = useState("");
	const [onlyApproved, setOnlyApproved] = useState(true);
	const [onlyAssigned, setOnlyAssigned] = useState(false);

	const fetchClients = async () => {
		try {
			setLoading(true);
			const clientsRes = await get_clients_name_list_agent_panel();
			setClients(
				(clientsRes ?? []).map((r) => ({ value: r.id, label: r.name })),
			);
		} catch (e) {
			console.error(e);
			message.error("No se pudieron cargar clientes.");
		} finally {
			setLoading(false);
		}
	};

	const fetchTemplates = async (cid) => {
		if (!cid) return;
		try {
			setLoading(true);

			const list = await get_templates_catalog({
				clientId: cid,
				onlyApproved,
				search,
			});

			const normalized = (list || []).map((t) => ({
				...t,
				id: t.id ?? t.Id,
				name: t.name ?? t.Name,
				metaTemplateId: t.metaTemplateId ?? t.MetaTemplateId,
				language: t.language ?? t.Language,
				status: t.status ?? t.Status,
				category: t.category ?? t.Category,
				subCategory: t.subCategory ?? t.SubCategory,
				parameterFormat: t.parameterFormat ?? t.ParameterFormat,
				previewFull: t.previewFull ?? t.PreviewFull,
				previewBody: t.previewBody ?? t.PreviewBody,

				isEnabled: !!(t.isEnabled ?? t.IsEnabled),
				displayName: (t.displayName ?? t.DisplayName) || (t.name ?? t.Name),
			}));

			setTemplates(normalized);
			setDirty(false);
		} catch (e) {
			console.error(e);
			message.error("No se pudieron cargar plantillas (catálogo).");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchClients();
	}, []);

	useEffect(() => {
		if (clientId) fetchTemplates(clientId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clientId]);

	useEffect(() => {
		if (clientId) fetchTemplates(clientId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onlyApproved]);

	const onToggleEnabled = (row, v) => {
		setTemplates((prev) =>
			prev.map((t) =>
				t.id === row.id
					? {
							...t,
							isEnabled: v,
							displayName: (t.displayName || t.name || "").trim() || t.name,
						}
					: t,
			),
		);
		setDirty(true);
	};

	const onChangeDisplayName = (row, v) => {
		setTemplates((prev) =>
			prev.map((t) => (t.id === row.id ? { ...t, displayName: v } : t)),
		);
		setDirty(true);
	};

	const handleSync = async () => {
		if (!clientId) return message.info("Seleccioná un cliente.");
		try {
			setSyncLoading(true);
			await sync_client_templates(clientId);
			await fetchTemplates(clientId);
			message.success("Plantillas actualizadas desde Meta.");
		} catch (e) {
			console.error(e);
			message.error("No se pudo actualizar desde Meta.");
		} finally {
			setSyncLoading(false);
		}
	};

	const handleSearch = async () => {
		if (!clientId) return message.info("Seleccioná un cliente.");
		await fetchTemplates(clientId);
	};

	const handleSave = async () => {
		if (!clientId) return message.info("Seleccioná un cliente.");

		const enabled = (templates || []).filter((t) => t.isEnabled);

		if (enabled.length === 0) {
			Modal.confirm({
				title: "Sin plantillas habilitadas",
				content:
					"Esto va a dejar al cliente sin plantillas asignadas. ¿Querés guardar igual?",
				okText: "Sí, guardar",
				cancelText: "Cancelar",
				onOk: async () => {
					await doSave([]);
				},
			});
			return;
		}

		await doSave(enabled);
	};

	const doSave = async (enabledTemplates) => {
		try {
			setLoading(true);

			const payload = {
				clientId,
				templates: enabledTemplates.map((t) => ({
					id: t.id,
					displayName: (t.displayName || t.name || "").trim(),
					isEnabled: true,
				})),
			};

			await set_client_templates(payload);

			message.success("Asignación guardada.");
			setDirty(false);
			await fetchTemplates(clientId);
		} catch (e) {
			console.error(e);
			message.error("No se pudo guardar la asignación.");
		} finally {
			setLoading(false);
		}
	};

	const dataSource = useMemo(() => {
		let list = [...(templates || [])];

		if (onlyAssigned) list = list.filter((t) => t.isEnabled);

		const s = (search || "").trim().toLowerCase();
		if (s) {
			list = list.filter((t) => {
				const hay =
					`${t.name} ${t.displayName} ${t.category} ${t.language} ${t.status}`.toLowerCase();
				return hay.includes(s);
			});
		}

		return list;
	}, [templates, onlyAssigned, search]);

	const columns = useMemo(
		() => [
			{
				title: "Plantilla",
				dataIndex: "name",
				render: (v, r) => (
					<Space direction="vertical" size={0}>
						<Typography.Text strong>{v}</Typography.Text>
						<Typography.Text type="secondary" style={{ fontSize: 12 }}>
							{r.metaTemplateId ? `MetaId: ${r.metaTemplateId}` : null}
						</Typography.Text>
					</Space>
				),
			},
			{
				title: "Idioma",
				dataIndex: "language",
				width: 110,
				render: (v) => <Tag style={{ borderRadius: 5 }}>{v || "—"}</Tag>,
			},
			{
				title: "Estado",
				dataIndex: "status",
				width: 120,
				render: (v) => (
					<Tag
						color={
							v === "APPROVED" ? "green" : v === "REJECTED" ? "red" : "orange"
						}
						style={{ borderRadius: 5 }}
					>
						{v || "—"}
					</Tag>
				),
			},
			{
				title: "Categoría",
				dataIndex: "category",
				width: 140,
				render: (v) => <Tag style={{ borderRadius: 5 }}>{v || "—"}</Tag>,
			},
			{
				title: "Asignada",
				dataIndex: "isEnabled",
				width: 110,
				render: (v, r) => (
					<AntdCheckbox
						checked={!!v}
						onChange={(e) => onToggleEnabled(r, e.target.checked)}
						disabled={r.status !== "APPROVED"}
					/>
				),
			},
			{
				title: "Nombre a mostrar",
				dataIndex: "displayName",
				width: 320,
				render: (_, r) => (
					<Input
						value={r.displayName || ""}
						placeholder="Ej: Primer mensaje / Carrito pendiente"
						onChange={(e) => onChangeDisplayName(r, e.target.value)}
						disabled={!r.isEnabled}
						style={{ borderRadius: 12 }}
					/>
				),
			},
			{
				title: "Preview",
				key: "preview",
				width: 90,
				render: (_, r) => (
					<Section>
						<Button
							icon={<EyeOutlined />}
							onClick={() => {
								setPreviewTemplate(r);
								setPreviewOpen(true);
							}}
							style={{ borderRadius: 12 }}
						/>
					</Section>
				),
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[templates],
	);

	return (
		<Inner>
			<Main.Header>
				<Main.Breadcrumb>
					<Main.BreadcrumbItem>
						{uri ? (
							<Link to={"/" + uri}>Configuración General</Link>
						) : (
							<Link to="/">Home</Link>
						)}
					</Main.BreadcrumbItem>
					<Main.BreadcrumbSeparator />
					<Main.ActiveBreadcrumbItem>Plantillas meta</Main.ActiveBreadcrumbItem>
				</Main.Breadcrumb>
			</Main.Header>
			<Card
				title="Asignación de plantillas de Meta"
				extra={
					<Space>
						<DefaultButton
							icon={<ReloadOutlined />}
							onClick={handleSync}
							loading={syncLoading}
							disabled={!clientId}
							style={{ height: 32, fontWeight: 400 }}
						>
							Actualizar desde Meta
						</DefaultButton>
						<PrimaryButton
							icon={<SaveOutlined />}
							onClick={handleSave}
							disabled={!clientId || !dirty}
							loading={loading}
							style={{ height: 32, fontWeight: 400 }}
						>
							Guardar
						</PrimaryButton>
					</Space>
				}
			>
				<Space direction="vertical" style={{ width: "100%" }} size={12}>
					<Space wrap>
						<Typography.Text>Cliente:</Typography.Text>
						<AntdSelect
							style={{ width: 420 }}
							placeholder="Seleccionar cliente"
							value={clientId}
							onChange={(v) => setClientId(v)}
							loading={loading && clients.length === 0}
							showSearch
							optionFilterProp="label"
							options={clients}
						/>

						<Tag color={dirty ? "gold" : "default"} style={{ borderRadius: 5 }}>
							{dirty ? "Cambios sin guardar" : "Sin cambios"}
						</Tag>
					</Space>

					<Space wrap>
						<Input
							style={{ width: 360, borderRadius: 12 }}
							placeholder="Buscar por nombre / categoría / idioma..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							allowClear
						/>
						<DefaultButton
							icon={<SearchOutlined />}
							onClick={handleSearch}
							disabled={!clientId}
							loading={loading}
							style={{ height: 32, fontWeight: 400 }}
						>
							Buscar
						</DefaultButton>

						<Space>
							<Typography.Text type="secondary">Solo APPROVED</Typography.Text>
							<Switch checked={onlyApproved} onChange={setOnlyApproved} />
						</Space>

						<Space>
							<Typography.Text type="secondary">Solo asignadas</Typography.Text>
							<Switch checked={onlyAssigned} onChange={setOnlyAssigned} />
						</Space>
					</Space>

					<Table
						loading={loading}
						rowKey={(r) => r.id}
						dataSource={dataSource}
						columns={columns}
						pagination={{ pageSize: 15 }}
					/>
				</Space>

				<Modal
					open={previewOpen}
					onCancel={() => setPreviewOpen(false)}
					footer={null}
					title={`Vista previa • ${previewTemplate?.name || ""}`}
				>
					{previewTemplate ? (
						<div
							style={{
								background: "#fafafa",
								border: "1px solid #eee",
								borderRadius: 10,
								padding: 12,
								whiteSpace: "pre-wrap",
								fontFamily: "monospace",
							}}
						>
							{previewTemplate.previewFull ||
								previewTemplate.previewBody ||
								"Sin preview"}
						</div>
					) : null}
				</Modal>
			</Card>
		</Inner>
	);
}
